/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';

export interface SupportContractListResponse {
    statusCode: number;
    data: string[];
    message?: string;
}

export interface SupportRequestType {
    id: string | number;
    name: string;
}

export interface SupportRequestTypesResponse {
    statusCode: number;
    data: SupportRequestType[];
    message?: string;
}

export interface CreateSupportTicketPayload {
    subject: string;
    description: string;
    contractId: string;
    requestType: string | number;
    file?: File;
    otherReason?: string | null | undefined;
}

export interface CreateSupportTicketResponse {
    statusCode: number;
    data?: any;
    message?: string;
}

export interface SupportTicketItem {
    Id?: string;
    CaseId?: string;
    CaseNumber?: string;
    RequestType?: string;
    Status?: string | number;
    CreatedTime?: string;
    UpdatedTime?: string;
    Subject?: string;
    Description?: string;
    ContractId?: string;
    statusLable?: string;
}

export interface SupportListResponse {
    statusCode: number;
    data: SupportTicketItem[];
    message?: string;
    totalCount?: number;
    page?: number;
    limit?: number;
}

export interface FAQItem {
    id: string;
    portalId: number;
    question: string;
    answer: string;
    createdTime: string;
    updatedTime: string;
}

export interface SupportFaqsResponse {
    statusCode: number;
    data: {
        faqs: FAQItem[];
        count: number;
    };
    message?: string;
}

/**
 * Get contract list for support requests
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with contract IDs response
 */
export const getSupportContractList = async (showLoader: boolean = true): Promise<SupportContractListResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.SupportContractList,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData;
        } else if (Array.isArray(responseData)) {
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching support contract list:', error);
        throw error;
    }
};

/**
 * Get request types for support requests
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with request types response
 */
export const getSupportRequestTypes = async (showLoader: boolean = true): Promise<SupportRequestTypesResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.SupportRequestType,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData;
        } else if (Array.isArray(responseData)) {
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching support request types:', error);
        throw error;
    }
};

/**
 * Create a support request
 * @param payload - Support request payload
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with create request response
 */
export const createSupportTicket = async (
    payload: CreateSupportTicketPayload,
    showLoader: boolean = true,
): Promise<CreateSupportTicketResponse> => {
    try {
        let payLoadData: any;
        let headerType: string = api_service.ContentHeaders.Json;

        // Helper function to get valid otherReason value (trimmed string or null)
        const getValidOtherReason = (): string | null => {
            if (
                payload.otherReason !== null &&
                payload.otherReason !== undefined &&
                typeof payload.otherReason === 'string' &&
                payload.otherReason.trim() !== ''
            ) {
                return payload.otherReason.trim();
            }
            return null;
        };

        const validOtherReason = getValidOtherReason();

        // If file is present, use FormData
        if (payload.file) {
            const formData = new FormData();
            formData.append('subject', payload.subject);
            formData.append('description', payload.description);
            formData.append('contractId', payload.contractId);
            formData.append('requestType', String(payload.requestType));

            // Append otherReason only if it has a valid non-empty value
            if (validOtherReason !== null) {
                formData.append('otherReason', validOtherReason);
            }

            // Append the file
            formData.append('file', payload.file);

            payLoadData = formData;
            headerType = api_service.ContentHeaders.FormData;
        } else {
            // If no file, send as JSON
            payLoadData = {
                subject: payload.subject,
                description: payload.description,
                contractId: payload.contractId,
                requestType: payload.requestType,
            };

            // Only include otherReason if it has a valid non-empty value
            if (validOtherReason !== null) {
                payLoadData.otherReason = validOtherReason;
            }

            headerType = api_service.ContentHeaders.Json;
        }

        const response: any = await api_service.post({
            endPoint: api_urls.CreateSupportTicket,
            payLoad: payLoadData,
            headerType,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode) {
            return responseData;
        }
        return { statusCode: 200, data: responseData, message: 'Support request created successfully' };
    } catch (error: any) {
        console.error('Error creating support request:', error);
        throw error;
    }
};

/**
 * Get support requests list
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with support list response
 */
export const getSupportList = async (
    showLoader: boolean = true,
    page: number = 1,
    limit: number = 10,
): Promise<SupportListResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.SupportList,
            showLoader,
            payLoad: {
                page,
                limit,
            },
        });
        const responseData = response?.data || response;

        if (responseData.statusCode && Array.isArray(responseData.data)) {
            return responseData;
        } else if (Array.isArray(responseData)) {
            return { statusCode: 200, data: responseData };
        }
        return { statusCode: 200, data: [] };
    } catch (error: any) {
        console.error('Error fetching support list:', error);
        throw error;
    }
};

/**
 * Get FAQs list for support
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with FAQs response
 */
export const getSupportFaqs = async (showLoader: boolean = true): Promise<SupportFaqsResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.SupportFaqsList,
            showLoader,
        });
        const responseData = response?.data || response;

        if (responseData.statusCode && responseData.data) {
            return responseData;
        }
        return { statusCode: 200, data: { faqs: [], count: 0 } };
    } catch (error: any) {
        console.error('Error fetching support FAQs:', error);
        throw error;
    }
};
